import { useSearchParams } from 'react-router-dom';
import { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useGameStore } from '@/stores/gameStore';
import { useCurrentSession } from '@/stores/gameSessionStore';
import { GameItem } from '@/types';
import ItemDebugDisplay from '@/components/ItemDebugDisplay';
import { AVAILABLE_BONUS_ITEMS } from '@/constants/bonusItems';
import { audioManager } from '@/utils/audioManager';
import { ITEM_SOUNDS } from '@/config/soundEffects';
import useSessionPhaseTimer from '@/hooks/useSessionPhaseTimer';
import { authService } from '@/services/auth';
import { useSafeNavigate } from '@/hooks/useSafeNavigate';

// Scaling constants - design dimensions for the bonus content
const CONTENT_WIDTH = 800;
const CONTENT_HEIGHT = 700;
const MAX_CONTENT_SCALE = 1.1; // Allow the bonus page to render up to 10% larger on roomy viewports
let lastAnimatedBonusItemsStageKey: string | null = null;

const getItemUsageInstructions = (item: GameItem): string => {
  switch (item.type) {
    case 'turbo':
      return 'Press SPACE to blast forward with a turbo sprint.';
    case 'net':
      return 'Press SPACE to throw a net and freeze opponents for 2 seconds.';
    case 'grenade':
      return 'Hold SHIFT to charge, then release to throw a grenade that pushes nearby players💥';
    case 'shoes':
      return 'Speed Shoes auto-activate — you will run 25% faster for the entire next round.';
    case 'shield':
      return 'This shield auto-activates to block the next incoming hit.';
    case 'gun':
      return 'Fire this gadget during the next round to disrupt nearby players.';
    case 'speed_boost':
      return 'This speed boost activates automatically during the next round.';
    default:
      return 'This bonus item activates during the next round.';
  }
};

const BonusItems = () => {
  const [searchParams] = useSearchParams();
  const playerId = searchParams.get('playerId');
  const sessionId = searchParams.get('sessionId');
  const navigate = useSafeNavigate();
  const currentSession = useCurrentSession();
  const {
    setSelectedBonusItem,
    addItemToPlayer,
    currentPlayerId,
    currentPlayer,
    hydrateSessionPlayers,
    setCurrentPlayerId,
    advanceToNextStage
  } = useGameStore();
  const gameMode = useGameStore(state => state.gameMode);
  const aiCurrentStage = useGameStore(state =>
    state.currentStageIndex >= 0 ? state.stages[state.currentStageIndex] : null
  );
  const isAiMode = gameMode === 'ai';
  const [showItems, setShowItems] = useState(false);
  const [selectedItem, setSelectedItem] = useState<GameItem | null>(null);
  const [isLockedIn, setIsLockedIn] = useState(false);
  const [isRevealing, setIsRevealing] = useState(false);
  const [cardFlipped, setCardFlipped] = useState(false);
  const [itemEntranceOrder, setItemEntranceOrder] = useState<Record<string, number>>({});

  // Scaling state for proportional viewport adjustment
  const [contentScale, setContentScale] = useState(1);
  const [contentOffset, setContentOffset] = useState({ x: 0, y: 0 });
  const debugVisible = false;
  const aiAdvanceTimeoutRef = useRef<number | null>(null);
  const lastSyncedItemRef = useRef<string | null>(null);
  const aiBonusAssignmentRef = useRef<string | null>(null);
  const studentBonusAssignmentRef = useRef<string | null>(null);
  const itemEntranceStageKeyRef = useRef<string | null>(null);
  const randomRevealStateRef = useRef<{ stageId: string | null; completed: boolean }>({
    stageId: null,
    completed: false
  });
  const randomRevealTimeoutRef = useRef<number | null>(null);
  const autoLockTimeoutRef = useRef<number | null>(null);
  const randomizableItemsRef = useRef<GameItem[]>([]);

  const sessionStageIndex = currentSession?.currentStageIndex ?? 0;
  const sessionStage = currentSession?.stages?.[sessionStageIndex];
  const sessionBonusRound = sessionStage?.type === 'bonus'
    ? sessionStage.bonusRound ?? null
    : null;
  const aiBonusRound = aiCurrentStage?.type === 'bonus'
    ? aiCurrentStage.bonusRound ?? null
    : null;
  const activeBonusRound = sessionBonusRound ?? aiBonusRound ?? null;
  const isRandomMode = Boolean(activeBonusRound?.assignRandomly);
  const activeStageId = sessionStage?.id ?? aiCurrentStage?.id ?? null;
  const entranceAnimationStageKey = useMemo(() => {
    if (isRandomMode || !activeStageId) {
      return null;
    }

    const stageOwnerId = isAiMode ? 'ai' : sessionId ?? currentSession?.id ?? 'session';
    return `${stageOwnerId}:${activeStageId}`;
  }, [activeStageId, currentSession?.id, isAiMode, isRandomMode, sessionId]);

  const availableItems: GameItem[] = useMemo(() => {
    if (activeBonusRound?.availableItems?.length) {
      const filtered = activeBonusRound.availableItems
        .map(itemId => {
          const item = AVAILABLE_BONUS_ITEMS.find(candidate => candidate.id === itemId);
          return item || null;
        })
        .filter((item): item is GameItem => item !== null);

      if (filtered.length > 0) {
        return filtered;
      }
    }

    return AVAILABLE_BONUS_ITEMS;
  }, [activeBonusRound]);

  const randomizableItems = useMemo(() => {
    const filtered = availableItems.filter(item => item.name !== 'Coming Soon');
    return filtered.length > 0 ? filtered : availableItems;
  }, [availableItems]);

  useEffect(() => {
    randomizableItemsRef.current = randomizableItems;
  }, [randomizableItems]);

  const sparklePositions = useMemo(
    () => [
      { left: '12%', top: '8%' },
      { left: '82%', top: '16%' },
      { left: '20%', top: '70%' },
      { left: '78%', top: '72%' },
      { left: '48%', top: '4%' },
      { left: '6%', top: '42%' },
      { left: '92%', top: '48%' }
    ],
    []
  );

  const getItemIcon = (item: GameItem) => {
    switch (item.type) {
      case 'net':
        return '🕸️';
      case 'shoes':
        return '👟';
      case 'turbo':
        return '⚡';
      case 'grenade':
        return '💣';
      case 'shield':
        return '🛡️';
      case 'gun':
        return '🔫';
      case 'speed_boost':
        return '💨';
      default:
        return '🎁';
    }
  };

  const fallbackSeconds = useMemo(() => {
    const ensurePositive = (value: number | undefined | null, fallback: number) => {
      if (typeof value === 'number' && value > 0) {
        return value;
      }
      return fallback;
    };

    if (currentSession) {
      const phaseDuration = currentSession.currentPhaseDurationSeconds;

      if (currentSession.gameState === 'bonus') {
        return ensurePositive(
          phaseDuration,
          ensurePositive(activeBonusRound?.selectionTimeSeconds, 8)
        );
      }

      return ensurePositive(activeBonusRound?.selectionTimeSeconds, 8);
    }

    return ensurePositive(activeBonusRound?.selectionTimeSeconds, 8);
  }, [currentSession, activeBonusRound]);

  // Use the timer hook
  const timeRemaining = useSessionPhaseTimer(currentSession, fallbackSeconds);

  useEffect(() => {
    if (!entranceAnimationStageKey) {
      setShowItems(false);
      setItemEntranceOrder({});
      itemEntranceStageKeyRef.current = null;
      return;
    }

    if (itemEntranceStageKeyRef.current !== entranceAnimationStageKey) {
      itemEntranceStageKeyRef.current = entranceAnimationStageKey;
      setItemEntranceOrder(Object.fromEntries(availableItems.map((item, index) => [item.id, index])));
    }

    if (lastAnimatedBonusItemsStageKey === entranceAnimationStageKey) {
      setShowItems(true);
      return;
    }

    lastAnimatedBonusItemsStageKey = entranceAnimationStageKey;
    setShowItems(false);

    const timer = window.setTimeout(() => {
      setShowItems(true);
    }, 500);

    return () => window.clearTimeout(timer);
  }, [availableItems, entranceAnimationStageKey]);

  useEffect(() => {
    if (!entranceAnimationStageKey || isRandomMode) {
      return;
    }

    setItemEntranceOrder(previousOrder => {
      if (availableItems.every(item => previousOrder[item.id] !== undefined)) {
        return previousOrder;
      }

      const nextOrder = { ...previousOrder };
      let nextIndex = Object.keys(nextOrder).length;

      availableItems.forEach(item => {
        if (nextOrder[item.id] === undefined) {
          nextOrder[item.id] = nextIndex;
          nextIndex += 1;
        }
      });

      return nextOrder;
    });
  }, [availableItems, entranceAnimationStageKey, isRandomMode]);

  useEffect(() => {
    if (currentSession?.players?.length) {
      hydrateSessionPlayers(currentSession.players, playerId);
    }
  }, [currentSession?.players, playerId, hydrateSessionPlayers]);

  useEffect(() => {
    // Reset the duplicate sync guard whenever the selection changes or a new player/session is targeted
    lastSyncedItemRef.current = null;
  }, [selectedItem?.id, playerId, sessionId]);

  useEffect(() => {
    if (!activeStageId) {
      studentBonusAssignmentRef.current = null;
      return;
    }

    if (studentBonusAssignmentRef.current && studentBonusAssignmentRef.current !== activeStageId) {
      studentBonusAssignmentRef.current = null;
    }
  }, [activeStageId]);

  useEffect(() => {
    if (!isAiMode) {
      return;
    }

    if (!activeStageId) {
      aiBonusAssignmentRef.current = null;
      return;
    }

    if (aiBonusAssignmentRef.current === activeStageId) {
      return;
    }

    const availableForAi = randomizableItemsRef.current.filter(item => item.name !== 'Coming Soon');
    if (availableForAi.length === 0) {
      return;
    }

    const storeState = useGameStore.getState();
    const aiPlayers = storeState.players.filter(player => player.id.startsWith('ai-player-'));

    if (aiPlayers.length === 0) {
      aiBonusAssignmentRef.current = activeStageId;
      return;
    }

    aiPlayers.forEach(aiPlayer => {
      const randomIndex = Math.floor(Math.random() * availableForAi.length);
      const selectedItem = availableForAi[randomIndex];

      if (!selectedItem) {
        return;
      }

      storeState.addItemToPlayer(aiPlayer.id, selectedItem);
    });

    aiBonusAssignmentRef.current = activeStageId;
  }, [activeStageId, isAiMode, randomizableItems]);

  useEffect(() => {
    if (playerId) {
      setCurrentPlayerId(playerId);
    }
  }, [playerId, setCurrentPlayerId]);

  const advanceAfterBonus = useCallback(() => {
    const nextStage = advanceToNextStage();

    if (!nextStage) {
      navigate('/final-scores');
      return;
    }

    if (nextStage.type === 'bonus') {
      navigate('/bonus');
      return;
    }

    navigate('/question');
  }, [advanceToNextStage, navigate]);

  const finalizeBonusItem = useCallback(async (item: GameItem) => {
    if (isLockedIn) {
      console.warn('⚠️ Attempted to lock in item multiple times. Ignoring.');
      return;
    }

    if (activeStageId && studentBonusAssignmentRef.current === activeStageId) {
      console.warn('⚠️ Bonus item already assigned for this stage. Ignoring additional selection.');
      return;
    }

    if (audioManager.isReady()) {
      audioManager.play(ITEM_SOUNDS.ITEM_PURCHASE.id);
    }

    setSelectedBonusItem(item);
    const targetPlayerId = currentPlayer?.id || currentPlayerId || playerId || 'current-player';

    const storeBeforeAssign = useGameStore.getState();
    const hasTargetPlayer = storeBeforeAssign.players.some(player => player.id === targetPlayerId)
      || storeBeforeAssign.currentPlayer?.id === targetPlayerId;

    if (!hasTargetPlayer && currentSession?.players?.length) {
      hydrateSessionPlayers(currentSession.players, playerId ?? targetPlayerId);
    }

    const didAssignItem = addItemToPlayer(targetPlayerId, item);
    if (!didAssignItem) {
      console.warn('⚠️ Failed to assign bonus item - player state not ready yet.');
      setSelectedBonusItem(null);
      setIsLockedIn(false);
      if (activeStageId) {
        studentBonusAssignmentRef.current = null;
      }
      return;
    }

    setIsLockedIn(true);
    if (activeStageId) {
      studentBonusAssignmentRef.current = activeStageId;
    }

    const { applyGameItemEffect } = useGameStore.getState();
    applyGameItemEffect(item);

    if (sessionId && playerId) {
      try {
        const storeState = useGameStore.getState();
        const resolvedPlayerId = targetPlayerId === 'current-player' && playerId
          ? playerId
          : targetPlayerId;
        const postUpdatePlayer = storeState.players.find(p => p.id === resolvedPlayerId)
          || storeState.players.find(p => p.id === targetPlayerId)
          || null;
        const currentPlayerInSession = currentSession?.players.find(p => p.id === playerId);
        const fallbackPlayer = postUpdatePlayer || currentPlayerInSession || null;

        const updates = {
          netCharges: Math.max(0, Math.floor(fallbackPlayer?.netCharges ?? 0)),
          grenadeCharges: Math.max(0, Math.floor(fallbackPlayer?.grenadeCharges ?? 0)),
          turboAvailable: Boolean(fallbackPlayer?.turboAvailable),
          turboUsed: Boolean(fallbackPlayer?.turboUsed),
          shoesEffectNextRound: Boolean(fallbackPlayer?.shoesEffectNextRound)
        };

        if (Object.keys(updates).length > 0) {
          const syncKey = `${playerId}:${item.type}:${JSON.stringify(updates)}`;

          if (lastSyncedItemRef.current !== syncKey) {
            await authService.updatePlayerItems(sessionId, playerId, updates);
            lastSyncedItemRef.current = syncKey;
          }
        }
      } catch (error) {
        lastSyncedItemRef.current = null;
        setIsLockedIn(false);
        console.error('❌ Failed to sync item to Firebase:', error);
        return;
      }
    } else if (!isAiMode) {
      console.warn('⚠️ Cannot sync item - missing sessionId or playerId');
      console.warn('  Session ID:', sessionId);
      console.warn('  Player ID:', playerId);
    }

    if (isAiMode) {
      if (aiAdvanceTimeoutRef.current) {
        window.clearTimeout(aiAdvanceTimeoutRef.current);
      }

      aiAdvanceTimeoutRef.current = window.setTimeout(() => {
        advanceAfterBonus();
      }, 1000);
    }
  }, [
    isLockedIn,
    currentPlayer,
    currentPlayerId,
    playerId,
    setSelectedBonusItem,
    addItemToPlayer,
    hydrateSessionPlayers,
    sessionId,
    currentSession,
    isAiMode,
    advanceAfterBonus,
    activeStageId
  ]);

  const finalizeBonusItemRef = useRef(finalizeBonusItem);
  useEffect(() => {
    finalizeBonusItemRef.current = finalizeBonusItem;
  }, [finalizeBonusItem]);

  const startRandomReveal = useCallback((stageId: string) => {
    if (randomRevealTimeoutRef.current) {
      window.clearTimeout(randomRevealTimeoutRef.current);
      randomRevealTimeoutRef.current = null;
    }

    if (autoLockTimeoutRef.current) {
      window.clearTimeout(autoLockTimeoutRef.current);
      autoLockTimeoutRef.current = null;
    }

    const items = randomizableItemsRef.current;

    if (!items.length) {
      console.warn('⚠️ Random mode enabled but no bonus items available to assign.');
      randomRevealStateRef.current = { stageId, completed: true };
      setIsRevealing(false);
      setCardFlipped(false);
      return;
    }

    // Set up the reveal state - the actual flip will happen via timeRemaining watcher
    randomRevealStateRef.current = { stageId, completed: false };
    setIsLockedIn(false);
    setSelectedItem(null);
    setIsRevealing(true);
    setCardFlipped(false);
  }, []);

  const handleLockIn = useCallback(async (itemOverride?: GameItem) => {
    const itemToLock = itemOverride ?? selectedItem;

    if (!itemToLock) {
      console.warn('⚠️ No item selected when trying to lock in');
      return;
    }

    await finalizeBonusItem(itemToLock);
  }, [selectedItem, finalizeBonusItem]);

  const handleItemSelect = (item: GameItem) => {
    if (isRandomMode || isRevealing) {
      return;
    }

    if (item.name !== 'Coming Soon' && !isLockedIn) {
      setSelectedItem(item);
    }
  };

  useEffect(() => {
    if (!isRandomMode) {
      randomRevealStateRef.current = { stageId: null, completed: false };
      setIsRevealing(false);
      setCardFlipped(false);
      return;
    }

    if (!activeStageId) {
      return;
    }

    if (isLockedIn) {
      randomRevealStateRef.current = { stageId: activeStageId, completed: true };
      return;
    }

    const state = randomRevealStateRef.current;

    if (state.stageId !== activeStageId) {
      startRandomReveal(activeStageId);
      return;
    }

    if (!state.completed && randomRevealTimeoutRef.current === null && autoLockTimeoutRef.current === null) {
      startRandomReveal(activeStageId);
    }
  }, [isRandomMode, activeStageId, isLockedIn, startRandomReveal]);

  // Auto-lock when timer reaches 0
  useEffect(() => {
    if (timeRemaining !== 0 || isLockedIn) {
      if (isRandomMode && timeRemaining === 0 && !isLockedIn && selectedItem) {
        if (activeStageId) {
          randomRevealStateRef.current = { stageId: activeStageId, completed: true };
        }
        void handleLockIn(selectedItem);
      }
      return;
    }

    if (isRandomMode) {
      const items = randomizableItemsRef.current;

      if (selectedItem) {
        if (activeStageId) {
          randomRevealStateRef.current = { stageId: activeStageId, completed: true };
        }
        void handleLockIn(selectedItem);
        return;
      }

      if (items.length > 0) {
        const fallbackItem = items[Math.floor(Math.random() * items.length)];
        console.warn('⚠️ Timer expired before reveal - assigning fallback item:', fallbackItem.name);
        setCardFlipped(true);
        setSelectedItem(fallbackItem);
        setIsRevealing(false);
        if (activeStageId) {
          randomRevealStateRef.current = { stageId: activeStageId, completed: true };
        }
        void handleLockIn(fallbackItem);
      } else {
        console.warn('⚠️ Timer expired in random mode but no items available to assign.');
      }

      return;
    }

    if (selectedItem) {
      void handleLockIn();
      return;
    }

    if (isAiMode) {
      setIsLockedIn(true);

      if (aiAdvanceTimeoutRef.current) {
        window.clearTimeout(aiAdvanceTimeoutRef.current);
      }

      aiAdvanceTimeoutRef.current = window.setTimeout(() => {
        advanceAfterBonus();
      }, 500);
    }
  }, [timeRemaining, selectedItem, isLockedIn, isAiMode, handleLockIn, advanceAfterBonus, isRandomMode, activeStageId]);

  // Watch timeRemaining to trigger card flip at the right moment
  useEffect(() => {
    if (!isRandomMode || !isRevealing || cardFlipped) {
      return;
    }

    // Calculate when to flip: 1 second after start means (totalTime - 1)
    const flipAtSeconds = fallbackSeconds - 1;

    if (timeRemaining <= flipAtSeconds && timeRemaining > 0) {
      const items = randomizableItemsRef.current;
      if (items.length > 0) {
        const randomIndex = Math.floor(Math.random() * items.length);
        const randomItem = items[randomIndex];

        setCardFlipped(true);
        setSelectedItem(randomItem);
        setIsRevealing(false);

        if (activeStageId) {
          randomRevealStateRef.current = { stageId: activeStageId, completed: true };
        }
      }
    }
  }, [timeRemaining, isRandomMode, isRevealing, cardFlipped, fallbackSeconds, activeStageId]);

  // Compute scaling transform based on viewport size
  const computeContentTransform = useCallback(() => {
    if (typeof window === 'undefined') return;

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Calculate scale to fit viewport while maintaining aspect ratio
    const rawScale = Math.min(
      viewportWidth / CONTENT_WIDTH,
      viewportHeight / CONTENT_HEIGHT
    );

    // Cap at 1 to prevent upscaling beyond design size
    const scale = Math.min(rawScale, MAX_CONTENT_SCALE);

    // Calculate centered position
    const scaledWidth = CONTENT_WIDTH * scale;
    const scaledHeight = CONTENT_HEIGHT * scale;
    const offsetX = (viewportWidth - scaledWidth) / 2;
    const offsetY = Math.max((viewportHeight - scaledHeight) / 2, 0);

    setContentScale(scale);
    setContentOffset({ x: offsetX, y: offsetY });
  }, []);

  // Recalculate on viewport resize
  useEffect(() => {
    computeContentTransform();

    if (typeof window === 'undefined') return;

    window.addEventListener('resize', computeContentTransform);
    const visualViewport = window.visualViewport;
    visualViewport?.addEventListener('resize', computeContentTransform);

    return () => {
      window.removeEventListener('resize', computeContentTransform);
      visualViewport?.removeEventListener('resize', computeContentTransform);
    };
  }, [computeContentTransform]);

  useEffect(() => {
    return () => {
      if (aiAdvanceTimeoutRef.current) {
        window.clearTimeout(aiAdvanceTimeoutRef.current);
      }
      if (randomRevealTimeoutRef.current) {
        window.clearTimeout(randomRevealTimeoutRef.current);
      }
      if (autoLockTimeoutRef.current) {
        window.clearTimeout(autoLockTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div style={{
      height: '100vh',
      width: '100vw',
      background: 'radial-gradient(ellipse at center, #1a0033 0%, #0d001a 100%)',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Animated Stars Background */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        background: `
          radial-gradient(2px 2px at 20px 30px, #fff, transparent),
          radial-gradient(2px 2px at 40px 70px, #fff, transparent),
          radial-gradient(1px 1px at 90px 40px, #fff, transparent),
          radial-gradient(1px 1px at 130px 80px, #fff, transparent),
          radial-gradient(2px 2px at 160px 30px, #fff, transparent)
        `,
        backgroundRepeat: 'repeat',
        backgroundSize: '200px 100px',
        animation: 'twinkle 4s ease-in-out infinite alternate'
      }} />

      {/* Magic Particles */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        background: `
          radial-gradient(circle, #ffd700 1px, transparent 1px),
          radial-gradient(circle, #ff69b4 1px, transparent 1px),
          radial-gradient(circle, #00ffff 1px, transparent 1px)
        `,
        backgroundSize: '50px 50px, 80px 80px, 120px 120px',
        backgroundPosition: '0 0, 20px 20px, 40px 40px',
        animation: 'float 6s ease-in-out infinite'
      }} />

      {/* Scaled Content Container */}
      <div style={{
        position: 'absolute',
        left: `${contentOffset.x}px`,
        top: `${contentOffset.y}px`,
        width: `${CONTENT_WIDTH}px`,
        height: `${CONTENT_HEIGHT}px`,
        transform: `scale(${contentScale})`,
        transformOrigin: 'top left',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center'
      }}>

      {/* Title */}
      <div style={{
        fontSize: '40px',
        fontWeight: 'bold',
        background: 'linear-gradient(45deg, #ffd700, #ff69b4, #00ffff, #ffd700)',
        backgroundSize: '400% 400%',
        backgroundClip: 'text',
        WebkitBackgroundClip: 'text',
        color: 'transparent',
        textAlign: 'center',
        marginBottom: '12px',
        animation: 'shimmer 3s ease-in-out infinite, bounce 2s ease-in-out infinite',
        textShadow: '0 0 30px rgba(255, 215, 0, 0.5)',
        zIndex: 10
      }}>
        ✨ BONUS ITEMS! ✨
      </div>

      {/* Subtitle */}
      <div style={{
        fontSize: '16px',
        color: '#ffd700',
        textAlign: 'center',
        marginBottom: '4px',
        animation: 'glow 2s ease-in-out infinite alternate',
        zIndex: 10
      }}>
        {isRandomMode
          ? 'Mystery cards are being assigned just for you!'
          : 'Choose your magical power-ups!'}
      </div>

      {/* Auto-lock hint */}
      <div style={{
        fontSize: '14px',
        color: '#ffffff',
        textAlign: 'center',
        marginBottom: '8px',
        opacity: 0.8,
        zIndex: 10
      }}>
        {isRandomMode
          ? isLockedIn && selectedItem
            ? `${selectedItem.name} is locked in!`
            : cardFlipped && selectedItem
              ? `You drew ${selectedItem.name}! Locking it in...`
              : 'Hang tight—we are drawing a surprise card for you!'
          : selectedItem
            ? `${selectedItem.name} selected! It will lock in when the timer ends.`
            : 'Click an item to select it!'}
      </div>

      {/* Timer */}
      <div style={{
        fontSize: '32px',
        color: timeRemaining <= 5 ? '#FF6B6B' : '#FFD700',
        fontWeight: 'bold',
        textAlign: 'center',
        marginBottom: '12px',
        animation: timeRemaining <= 5 ? 'pulse 1s ease-in-out infinite' : 'none',
        textShadow: timeRemaining <= 5 ? '0 0 20px rgba(255, 107, 107, 0.8)' : '0 0 20px rgba(255, 215, 0, 0.5)',
        zIndex: 10
      }}>
        {timeRemaining}s
      </div>

      {/* Random Reveal or Items Grid */}
      {isRandomMode ? (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '16px',
          marginBottom: '16px',
          zIndex: 10
        }}>
          <div style={{
            position: 'relative',
            width: '180px',
            height: '240px',
            perspective: '1400px'
          }}>
            <div style={{
              position: 'absolute',
              inset: '-24px',
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(255, 255, 255, 0.65) 0%, rgba(255, 255, 255, 0) 70%)',
              filter: 'blur(25px)',
              opacity: cardFlipped ? 0.9 : 0.6,
              pointerEvents: 'none',
              animation: 'cardAuraPulse 1.6s ease-in-out infinite'
            }} />
            <div style={{
              position: 'absolute',
              inset: '-18px',
              borderRadius: '50%',
              background: 'conic-gradient(from 0deg, rgba(255, 215, 0, 0.25), rgba(0, 255, 255, 0.35), rgba(255, 105, 180, 0.25), rgba(255, 215, 0, 0.25))',
              opacity: cardFlipped ? 0.7 : 0.45,
              pointerEvents: 'none',
              animation: 'cardSpin 8s linear infinite'
            }} />
            {cardFlipped && (
              <div style={{
                position: 'absolute',
                inset: '-42px',
                background: 'radial-gradient(circle, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0) 65%)',
                filter: 'blur(18px)',
                pointerEvents: 'none',
                animation: 'revealBurst 1.2s ease-out'
              }} />
            )}
            <div style={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              transformStyle: 'preserve-3d',
              transform: cardFlipped
                ? 'rotateY(180deg) scale(1)'
                : isRevealing
                  ? 'rotateY(0deg) scale(1.08)'
                  : 'rotateY(0deg) scale(1)',
              transition: 'transform 0.7s cubic-bezier(0.22, 0.61, 0.36, 1)'
            }}>
              <div style={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                backfaceVisibility: 'hidden',
                borderRadius: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.95) 0%, rgba(118, 75, 162, 0.95) 100%)',
                boxShadow: '0 15px 35px rgba(102, 126, 234, 0.45)',
                border: '2px solid rgba(255, 255, 255, 0.25)',
                color: '#ffffff',
                fontSize: '48px',
                letterSpacing: '3px',
                textShadow: '0 0 15px rgba(255, 255, 255, 0.45)'
              }}>
                ?
              </div>
              <div style={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                backfaceVisibility: 'hidden',
                transform: 'rotateY(180deg)',
                borderRadius: '16px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.98) 0%, rgba(255, 105, 180, 0.98) 50%, rgba(0, 255, 255, 0.95) 100%)',
                boxShadow: '0 15px 40px rgba(255, 185, 0, 0.45)',
                border: '2px solid rgba(255, 255, 255, 0.7)',
                padding: '16px',
                textAlign: 'center',
                color: '#2d0a3d',
                overflow: 'hidden'
              }}>
                {cardFlipped && (
                  <div style={{
                    position: 'absolute',
                    inset: '-30%',
                    background: 'conic-gradient(from 90deg, rgba(255, 255, 255, 0.45), rgba(255, 255, 255, 0))',
                    animation: 'cardSpin 6s linear infinite',
                    opacity: 0.4
                  }} />
                )}
                <div style={{
                  fontSize: '48px',
                  marginBottom: '8px',
                  animation: cardFlipped ? 'rewardPop 0.8s ease-out forwards' : 'none',
                  textShadow: '0 0 20px rgba(255, 255, 255, 0.5)'
                }}>
                  {selectedItem ? getItemIcon(selectedItem) : '🎁'}
                </div>
                <div style={{
                  fontSize: '16px',
                  fontWeight: 800,
                  marginBottom: '4px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em'
                }}>
                  {selectedItem?.name || '???'}
                </div>
                <div style={{
                  fontSize: '12px',
                  lineHeight: 1.4,
                  maxWidth: '90%',
                  opacity: 0.9
                }}>
                  {selectedItem?.description || 'Revealing...'}
                </div>
              </div>
            </div>
            {sparklePositions.map((sparkle, index) => (
              <div
                key={`spark-${index}`}
                style={{
                  position: 'absolute',
                  width: '12px',
                  height: '12px',
                  borderRadius: '50%',
                  background: 'radial-gradient(circle, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0) 65%)',
                  top: sparkle.top,
                  left: sparkle.left,
                  pointerEvents: 'none',
                  opacity: cardFlipped ? 0.85 : 0.6,
                  animation: `sparkleDrift 2.4s ease-in-out infinite`,
                  animationDelay: `${index * 0.18}s`
                }}
              />
            ))}
            {isRevealing && (
              <div style={{
                position: 'absolute',
                inset: '-18%',
                background: 'radial-gradient(circle, rgba(255, 255, 255, 0.9) 0%, rgba(255, 255, 255, 0) 60%)',
                filter: 'blur(28px)',
                animation: 'cardPulse 1.1s ease-in-out infinite alternate',
                pointerEvents: 'none'
              }} />
            )}
            <div style={{
              position: 'absolute',
              inset: 0,
              pointerEvents: 'none',
              borderRadius: '16px',
              border: '2px solid rgba(255, 255, 255, 0.5)',
              boxShadow: cardFlipped
                ? '0 0 20px rgba(0, 255, 136, 0.65), inset 0 0 25px rgba(255, 255, 255, 0.55)'
                : '0 0 25px rgba(118, 75, 162, 0.5)',
              animation: 'cardGlow 2.4s ease-in-out infinite alternate'
            }} />
          </div>
          {selectedItem && randomizableItems.length > 0 && (
            <div style={{
              color: '#ffffff',
              textAlign: 'center',
              maxWidth: '300px',
              opacity: cardFlipped ? 1 : 0,
              transition: 'opacity 0.4s ease'
            }}>
              <div style={{ fontSize: '18px', fontWeight: 700, marginBottom: '4px' }}>
                You drew {selectedItem.name}!
              </div>
              <div style={{ fontSize: '14px', opacity: 0.85 }}>
                {selectedItem.description}
              </div>
              <div style={{
                fontSize: '13px',
                marginTop: '8px',
                color: '#00ff88',
                fontWeight: 600
              }}>
                {getItemUsageInstructions(selectedItem)}
              </div>
            </div>
          )}
          {!randomizableItems.length && (
            <div style={{
              color: '#ffffff',
              opacity: 0.7,
              textAlign: 'center',
              fontSize: '13px'
            }}>
              No bonus items configured for this round.
            </div>
          )}
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '16px',
          maxWidth: '520px',
          width: '90%',
          marginBottom: '16px',
          zIndex: 10
        }}>
          {availableItems.map((item, index) => {
            const isSelected = selectedItem?.id === item.id;
            const isAvailable = item.name !== 'Coming Soon';
            const entranceIndex = itemEntranceOrder[item.id] ?? index;

            return (
              <div
                key={item.id}
                onClick={() => handleItemSelect(item)}
                style={{
                  width: '140px',
                  height: '140px',
                  background: isSelected
                    ? 'linear-gradient(135deg, rgba(255, 215, 0, 0.4) 0%, rgba(255, 105, 180, 0.4) 50%, rgba(0, 255, 255, 0.4) 100%)'
                    : 'linear-gradient(135deg, rgba(255, 215, 0, 0.2) 0%, rgba(255, 105, 180, 0.2) 50%, rgba(0, 255, 255, 0.2) 100%)',
                  border: isSelected ? '3px solid #00ff88' : '2px solid #ffd700',
                  borderRadius: '14px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  position: 'relative',
                  cursor: isAvailable ? 'pointer' : 'not-allowed',
                  transition: 'all 0.3s ease',
                  visibility: showItems ? 'visible' : 'hidden',
                  animation: showItems ? `itemAppear 0.8s ease-out ${entranceIndex * 0.2}s both` : 'none',
                  boxShadow: isSelected
                    ? '0 10px 25px rgba(0, 255, 136, 0.5), inset 0 0 20px rgba(255, 255, 255, 0.2)'
                    : '0 6px 20px rgba(255, 215, 0, 0.3), inset 0 0 15px rgba(255, 255, 255, 0.1)',
                  opacity: isAvailable ? 1 : 0.6
                }}
                onMouseEnter={(e) => {
                  if (isAvailable) {
                    e.currentTarget.style.transform = 'scale(1.05) rotateY(8deg)';
                    e.currentTarget.style.boxShadow = isSelected
                      ? '0 12px 35px rgba(0, 255, 136, 0.7), inset 0 0 25px rgba(255, 255, 255, 0.3)'
                      : '0 10px 30px rgba(255, 215, 0, 0.5), inset 0 0 20px rgba(255, 255, 255, 0.2)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (isAvailable) {
                    e.currentTarget.style.transform = 'scale(1) rotateY(0deg)';
                    e.currentTarget.style.boxShadow = isSelected
                      ? '0 10px 25px rgba(0, 255, 136, 0.5), inset 0 0 20px rgba(255, 255, 255, 0.2)'
                      : '0 6px 20px rgba(255, 215, 0, 0.3), inset 0 0 15px rgba(255, 255, 255, 0.1)';
                  }
                }}
              >
                {/* Magic sparkle effect */}
                <div style={{
                  position: 'absolute',
                  top: '6px',
                  right: '6px',
                  width: '14px',
                  height: '14px',
                  background: 'radial-gradient(circle, #ffd700 30%, transparent 70%)',
                  borderRadius: '50%',
                  animation: 'sparkle 2s ease-in-out infinite'
                }} />

                {/* Item icon/content */}
                <div style={{
                  fontSize: item.type === 'net' ? '40px' : '32px',
                  marginBottom: '5px',
                  animation: isAvailable ? 'pulse 3s ease-in-out infinite' : 'none'
                }}>
                  {getItemIcon(item)}
                </div>

                {/* Item name */}
                <div style={{
                  fontSize: '12px',
                  color: '#ffd700',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  marginBottom: '2px'
                }}>
                  {item.name}
                </div>

                {/* Item description */}
                <div style={{
                  fontSize: '10px',
                  color: '#ffffff',
                  textAlign: 'center',
                  opacity: 0.8,
                  padding: '0 5px',
                  lineHeight: '1.2'
                }}>
                  {item.description}
                </div>

                {/* Selection indicator */}
                {isSelected && (
                  <div style={{
                    position: 'absolute',
                    top: '-5px',
                    left: '-5px',
                    right: '-5px',
                    bottom: '-5px',
                    border: '2px solid #00ff88',
                    borderRadius: '18px',
                    animation: 'selectionGlow 2s ease-in-out infinite alternate'
                  }} />
                )}
              </div>
            );
          })}
        </div>
      )}

      {!isRandomMode && selectedItem && (
        <div style={{
          marginTop: '12px',
          color: '#00ff88',
          textAlign: 'center',
          fontSize: '14px',
          fontWeight: 600,
          maxWidth: '350px',
          lineHeight: 1.4,
          zIndex: 10
        }}>
          {getItemUsageInstructions(selectedItem)}
        </div>
      )}

      {/* Status message */}
      {isLockedIn && selectedItem && (
        <div style={{
          marginTop: '8px',
          color: '#00ff88',
          fontWeight: 'bold',
          fontSize: '14px',
          zIndex: 10
        }}>
          {isRandomMode
            ? `${selectedItem.name} locked in! 🎉`
            : `${selectedItem.name} locked in! Waiting for your teacher...`}
        </div>
      )}

      </div>{/* End Scaled Content Container */}

      {/* CSS Animations */}
      <style>{`
        @keyframes shimmer {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }

        @keyframes bounce {
          0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
          40% { transform: translateY(-10px); }
          60% { transform: translateY(-5px); }
        }

        @keyframes glow {
          0% { text-shadow: 0 0 10px rgba(255, 215, 0, 0.5); }
          100% { text-shadow: 0 0 20px rgba(255, 215, 0, 0.8), 0 0 30px rgba(255, 215, 0, 0.6); }
        }

        @keyframes twinkle {
          0% { opacity: 0.3; }
          100% { opacity: 1; }
        }

        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          33% { transform: translateY(-10px); }
          66% { transform: translateY(5px); }
        }

        @keyframes itemAppear {
          0% {
            opacity: 0;
            transform: scale(0.3) rotateY(180deg);
          }
          70% {
            opacity: 1;
            transform: scale(1.1) rotateY(-10deg);
          }
          100% {
            opacity: 1;
            transform: scale(1) rotateY(0deg);
          }
        }

        @keyframes sparkle {
          0%, 100% {
            opacity: 0;
            transform: scale(0.5) rotate(0deg);
          }
          50% {
            opacity: 1;
            transform: scale(1.2) rotate(180deg);
          }
        }

        @keyframes pulse {
          0%, 100% { opacity: 0.5; transform: scale(1); }
          50% { opacity: 0.8; transform: scale(1.1); }
        }

        @keyframes buttonGlow {
          0% { box-shadow: 0 10px 25px rgba(255, 107, 53, 0.4); }
          100% { box-shadow: 0 10px 25px rgba(255, 107, 53, 0.6), 0 0 20px rgba(255, 107, 53, 0.5); }
        }

        @keyframes cardAuraPulse {
          0% { transform: scale(0.92); opacity: 0.5; }
          50% { transform: scale(1.05); opacity: 0.85; }
          100% { transform: scale(0.95); opacity: 0.55; }
        }

        @keyframes cardSpin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        @keyframes revealBurst {
          0% { transform: scale(0.5); opacity: 0.9; }
          60% { transform: scale(1.05); opacity: 0.7; }
          100% { transform: scale(1.3); opacity: 0; }
        }

        @keyframes sparkleDrift {
          0% { transform: translate3d(0, 0, 0) scale(0.7); opacity: 0; }
          30% { opacity: 1; }
          70% { transform: translate3d(6px, -14px, 0) scale(1.1); opacity: 0.8; }
          100% { transform: translate3d(-4px, -22px, 0) scale(0.8); opacity: 0; }
        }

        @keyframes cardGlow {
          0% {
            box-shadow: 0 0 25px rgba(118, 75, 162, 0.4), inset 0 0 18px rgba(255, 255, 255, 0.2);
          }
          100% {
            box-shadow: 0 0 40px rgba(0, 255, 136, 0.6), inset 0 0 30px rgba(255, 255, 255, 0.35);
          }
        }

        @keyframes cardPulse {
          0% { opacity: 0.4; transform: scale(0.9); }
          100% { opacity: 1; transform: scale(1.05); }
        }

        @keyframes rewardPop {
          0% { transform: scale(0.4) rotate(-10deg); opacity: 0; }
          60% { transform: scale(1.2) rotate(6deg); opacity: 1; }
          100% { transform: scale(1) rotate(0deg); opacity: 1; }
        }

        @keyframes selectionGlow {
          0% {
            box-shadow: 0 0 20px rgba(0, 255, 136, 0.6);
            border-color: #00ff88;
          }
          100% {
            box-shadow: 0 0 30px rgba(0, 255, 136, 0.9), 0 0 40px rgba(0, 255, 136, 0.4);
            border-color: #44ffaa;
          }
        }
      `}</style>

      {/* Item Debug Display */}
      <ItemDebugDisplay position="top-right" isVisible={debugVisible} />

    </div>
  );
};

export default BonusItems;
